function registerAlpineComponents() {
    if (typeof Alpine === 'undefined') {
        console.warn('Alpine.js not loaded yet');
        return;
    }

    Alpine.store('cart', {
        items: [],
        count: 0,
        total: 0,
        isOpen: false,
        isLoading: false,

        init() {
            const checkAndFetch = () => {
                const isLoggedIn = (typeof frappe !== 'undefined') &&
                                   frappe.session &&
                                   frappe.session.user &&
                                   frappe.session.user !== 'Guest';
                if (isLoggedIn) {
                    this.fetchCart();
                }
            };

            if (typeof frappe !== 'undefined' && frappe.ready) {
                frappe.ready(checkAndFetch);
            } else {
                if (document.readyState === 'complete') {
                    setTimeout(checkAndFetch, 100);
                } else {
                    document.addEventListener('DOMContentLoaded', () => {
                        setTimeout(checkAndFetch, 100);
                    });
                }
            }
        },

        async fetchCart() {
            const isLoggedIn = (typeof frappe !== 'undefined') &&
                               frappe.session &&
                               frappe.session.user &&
                               frappe.session.user !== 'Guest';
            if (!isLoggedIn) {
                return;
            }
            try {
                const response = await frappe.call({
                    method: 'tos_webcommerce.api.cart.get_cart_quotation',
                    freeze: false
                });
                if (response.message) {
                    this.items = response.message.doc?.items || [];
                    this.count = this.items.reduce((sum, item) => sum + item.qty, 0);
                    this.total = response.message.doc?.grand_total || 0;
                }
            } catch (error) {
                console.error('Failed to fetch cart:', error);
            }
        },

        async postAddToCart(itemCode, qty) {
            this.isLoading = true;
            try {
                await frappe.call({
                    method: 'tos_webcommerce.api.cart.update_cart',
                    args: {
                        item_code: itemCode,
                        qty: qty
                    },
                    freeze: true,
                    freeze_message: 'Adding to cart...'
                });
                await this.fetchCart();
                frappe.show_alert({
                    message: 'Item added to cart',
                    indicator: 'green'
                });
            } catch (error) {
                frappe.show_alert({
                    message: 'Failed to add item',
                    indicator: 'red'
                });
            } finally {
                this.isLoading = false;
            }
        },

        async clearCart() {
            if (!this.items || !this.items.length) return;
            try {
                this.isLoading = true;
                await frappe.call({
                    method: 'tos_webcommerce.api.cart.clear_cart',
                    freeze: true,
                    freeze_message: 'Mengosongkan keranjang...',
                });
                await this.fetchCart();
            } finally {
                this.isLoading = false;
            }
        },

        async addItem(itemCode, qty = 1) {
            const check = await new Promise((resolve, reject) => {
                frappe.call({
                    method: 'tos_webcommerce.stock.validate_cart_fulfillability',
                    args: { item_code: itemCode, qty: qty },
                    callback: (r) => resolve(r.message),
                    error: reject,
                });
            });

            if (!check || check.ok) {
                return this.postAddToCart(itemCode, qty);
            }

            if (check.reason === 'qty_exceeds_max') {
                const cap = check.alternatives.max_qty_current_warehouses;
                frappe.show_alert({ message: `Stok maksimum yang bisa dikirim dari satu lokasi: ${cap}.`, indicator: 'orange' });
                if (cap > 0) {
                    return this.postAddToCart(itemCode, cap);
                }
                return;
            }

            if (check.reason === 'out_of_stock') {
                frappe.show_alert({ message: 'Item sudah tidak tersedia.', indicator: 'red' });
                return;
            }

            if (check.reason === 'cross_warehouse_conflict') {
                const conflictingItems = check.conflicting_items || [];
                const warehousesWithNewItem = (check.alternatives && check.alternatives.warehouses_with_new_item) || [];
                const action = await window.tosShowConflictDialog({
                    title: 'Item tidak bisa digabung',
                    paragraphs: [
                        {
                            parts: [
                                { text: 'Item ' },
                                { text: itemCode, strong: true },
                                { text: ' tidak tersedia di gudang yang sama dengan item di keranjang kamu (' + conflictingItems.join(', ') + ').' },
                            ],
                        },
                        'Item ini tersedia di: ' + (warehousesWithNewItem.length ? warehousesWithNewItem.join(', ') : '—'),
                    ],
                    actions: [
                        { label: 'Checkout pesanan saat ini dulu', value: 'checkout_first', cls: 'btn btn-primary' },
                        { label: 'Kosongkan keranjang & pesan item ini saja', value: 'clear_and_add', cls: 'btn btn-secondary' },
                        { label: 'Batal', value: 'cancel', cls: 'btn btn-link' },
                    ],
                });
                if (action === 'checkout_first') {
                    window.location.href = '/checkout';
                    return;
                }
                if (action === 'clear_and_add') {
                    await this.clearCart();
                    return this.postAddToCart(itemCode, qty);
                }
                return; // cancel
            }

            frappe.show_alert({ message: 'Tidak bisa menambahkan item ke keranjang.', indicator: 'red' });
        },

        toggle() {
            this.isOpen = !this.isOpen;
        },

        open() {
            this.isOpen = true;
        },

        close() {
            this.isOpen = false;
        }
    });

    Alpine.store('search', {
        query: '',
        suggestions: [],
        recentSearches: [],
        isOpen: false,
        isLoading: false,
        debounceTimer: null,

        init() {
            const stored = localStorage.getItem('recentSearches');
            if (stored) {
                this.recentSearches = JSON.parse(stored).slice(0, 5);
            }
        },

        async search(query) {
            this.query = query;

            if (query.length < 2) {
                this.suggestions = [];
                this.isLoading = false;
                return;
            }

            clearTimeout(this.debounceTimer);

            const searchQuery = query;

            this.debounceTimer = setTimeout(async () => {
                this.isLoading = true;
                this.suggestions = [];
                try {
                    const response = await frappe.call({
                        method: 'tos_webcommerce.api.product.search_products',
                        type: 'POST', 
                        args: {
                            query: searchQuery,
                            limit: 6
                        },
                        freeze: false,
                        async: true
                    });

                    if (this.query === searchQuery) {
                        this.suggestions = response.message || [];
                    }
                } catch (error) {
                    console.error('Search failed:', error);
                    if (this.query === searchQuery) {
                        this.suggestions = [];
                    }
                } finally {
                    if (this.query === searchQuery) {
                        this.isLoading = false;
                    }
                }
            }, 300);
        },

        saveSearch(query) {
            if (!query || query.length < 2) return;

            this.recentSearches = [
                query,
                ...this.recentSearches.filter(s => s !== query)
            ].slice(0, 5);

            localStorage.setItem('recentSearches', JSON.stringify(this.recentSearches));
        },

        clearRecentSearches() {
            this.recentSearches = [];
            localStorage.removeItem('recentSearches');
        },

        submit() {
            if (this.query.length >= 2) {
                this.saveSearch(this.query);
                window.location.href = `/shop?search=${encodeURIComponent(this.query)}#catalog`;
            }
        },

        open() {
            this.isOpen = true;
        },

        close() {
            this.isOpen = false;
            this.suggestions = [];
        }
    });

    Alpine.store('wishlist', {
        items: [],
        isLoading: false,

        init() {
            const isLoggedIn = typeof frappe !== 'undefined' &&
                               frappe.session?.user &&
                               frappe.session.user !== 'Guest';
            if (isLoggedIn) {
                this.fetchWishlist();
            }
        },

        async fetchWishlist() {
            try {
                const response = await frappe.call({
                    method: 'tos_webcommerce.commerce.api.wishlist',
                    freeze: false
                });
                if (response.message) {
                    this.items = response.message.map(item => item.item_code);
                }
            } catch (error) {
                console.error('Failed to fetch wishlist:', error);
            }
        },

        async toggle(itemCode) {
            const isLoggedIn = typeof frappe !== 'undefined' &&
                               frappe.session?.user &&
                               frappe.session.user !== 'Guest';

            if (!isLoggedIn) {
                frappe.show_alert({
                    message: 'Please login to use wishlist',
                    indicator: 'orange'
                });
                window.location.href = '/login?redirect-to=' + encodeURIComponent(window.location.pathname);
                return;
            }

            const index = this.items.indexOf(itemCode);
            this.isLoading = true;

            try {
                if (index > -1) {
                    // Remove from wishlist
                    await frappe.call({
                        method: 'tos_webcommerce.commerce.api.update_wishlist',
                        args: { item_code: itemCode, action: 'remove' }
                    });
                    this.items.splice(index, 1);
                    frappe.show_alert({ message: 'Removed from wishlist', indicator: 'orange' });
                } else {
                    // Add to wishlist
                    await frappe.call({
                        method: 'tos_webcommerce.commerce.api.update_wishlist',
                        args: { item_code: itemCode, action: 'add' }
                    });
                    this.items.push(itemCode);
                    frappe.show_alert({ message: 'Added to wishlist', indicator: 'green' });
                }
            } catch (error) {
                frappe.show_alert({ message: 'Failed to update wishlist', indicator: 'red' });
            } finally {
                this.isLoading = false;
            }
        },

        has(itemCode) {
            return this.items.includes(itemCode);
        },

        async clear() {
            await frappe.call({
                method: 'tos_webcommerce.commerce.api.update_wishlist',
                args: { action: 'clear' }
            });
            this.items = [];
        }
    });

    Alpine.store('ui', {
        mobileMenuOpen: false,
        quickViewOpen: false,
        quickViewProduct: null,
        scrolled: false,

        init() {
            window.addEventListener('scroll', () => {
                this.scrolled = window.scrollY > 50;
            });
        },

        toggleMobileMenu() {
            this.mobileMenuOpen = !this.mobileMenuOpen;
            document.body.style.overflow = this.mobileMenuOpen ? 'hidden' : '';
        },

        closeMobileMenu() {
            this.mobileMenuOpen = false;
            document.body.style.overflow = '';
        },

        openQuickView(product) {
            this.quickViewProduct = product;
            this.quickViewOpen = true;
            document.body.style.overflow = 'hidden';
        },

        closeQuickView() {
            this.quickViewOpen = false;
            this.quickViewProduct = null;
            document.body.style.overflow = '';
        }
    });

    Alpine.data('flashSaleCountdown', (endDateStr) => ({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        expired: false,
        endDate: null,
        interval: null,

        init() {
            if (!endDateStr) {
                this.expired = true;
                return;
            }

            this.endDate = new Date(endDateStr);
            this.updateCountdown();
            this.interval = setInterval(() => this.updateCountdown(), 1000);
        },

        updateCountdown() {
            const now = new Date();
            const diff = this.endDate - now;

            if (diff <= 0) {
                this.expired = true;
                clearInterval(this.interval);
                this.days = 0;
                this.hours = 0;
                this.minutes = 0;
                this.seconds = 0;
                return;
            }

            this.days = Math.floor(diff / (1000 * 60 * 60 * 24));
            this.hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            this.minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            this.seconds = Math.floor((diff % (1000 * 60)) / 1000);
        },

        destroy() {
            if (this.interval) {
                clearInterval(this.interval);
            }
        },

        pad(num) {
            return num.toString().padStart(2, '0');
        }
    }));

    Alpine.data('productCard', (product) => ({
        addingToCart: false,

        getItemCode() {
            return product.itemCode || product.item_code || product.name;
        },

        // Qty lives in the cart store, not in the card — every card showing the same item
        // stays in sync and a reload keeps the stepper correct.
        cartQty() {
            const code = this.getItemCode();
            const line = (Alpine.store('cart').items || []).find(i => i.item_code === code);
            return line ? line.qty : 0;
        },

        requireLogin() {
            const isLoggedIn = (typeof frappe !== 'undefined') &&
                               frappe.session &&
                               frappe.session.user &&
                               frappe.session.user !== 'Guest';

            if (!isLoggedIn) {
                frappe.show_alert({
                    message: 'Silakan masuk untuk menambahkan produk ke keranjang.',
                    indicator: 'orange'
                });
                window.location.href = '/login?redirect-to=' + encodeURIComponent(window.location.pathname);
            }
            return isLoggedIn;
        },

        async addToCart() {
            if (this.addingToCart) return;
            if (!this.requireLogin()) return;

            this.addingToCart = true;
            try {
                // update_cart sets an ABSOLUTE qty, so send current + 1 — passing 1 would
                // silently reset a line that already has qty 3 back down to 1.
                await Alpine.store('cart').addItem(this.getItemCode(), this.cartQty() + 1);
            } finally {
                this.addingToCart = false;
            }
        },

        async decrement() {
            if (this.addingToCart) return;
            if (!this.requireLogin()) return;

            const next = this.cartQty() - 1;
            this.addingToCart = true;
            try {
                // Going down never needs the fulfillability check — qty 0 removes the line.
                await Alpine.store('cart').postAddToCart(this.getItemCode(), Math.max(next, 0));
            } finally {
                this.addingToCart = false;
            }
        },

        toggleWishlist() {
            Alpine.store('wishlist').toggle(this.getItemCode());
        },

        isInWishlist() {
            return Alpine.store('wishlist').has(this.getItemCode());
        },

        quickView() {
            Alpine.store('ui').openQuickView(product);
        }
    }));

    Alpine.store('shopFilter', {
        searchQuery: '',
        selectedCategory: '',
        sortBy: 'name',
        inStockOnly: false,
        priceMin: 0,
        priceMax: 0,
        categories: [],
        isFiltering: false,
        debounceTimer: null,

        init() {
            const params = new URLSearchParams(window.location.search);
            this.searchQuery = params.get('search') || '';
            this.selectedCategory = params.get('category') || '';
            this.sortBy = params.get('sort') || 'name';
            this.inStockOnly = params.get('in_stock') === '1';
        },

        setCategory(category) {
            this.selectedCategory = category;
            this.applyFilters();
        },

        setSort(sort) {
            this.sortBy = sort;
            this.applyFilters();
        },

        toggleInStock() {
            this.inStockOnly = !this.inStockOnly;
            this.applyFilters();
        },

        setSearch(query) {
            this.searchQuery = query;
        },

        clearSearch() {
            this.searchQuery = '';
            this.applyFilters();
        },

        clearCategory() {
            this.selectedCategory = '';
            this.applyFilters();
        },

        clearAll() {
            this.searchQuery = '';
            this.selectedCategory = '';
            this.sortBy = 'name';
            this.inStockOnly = false;
            this.applyFilters();
        },

        applyFilters() {
            this.isFiltering = true;
            const params = new URLSearchParams();

            if (this.searchQuery) params.set('search', this.searchQuery);
            if (this.selectedCategory) params.set('category', this.selectedCategory);
            if (this.selectedBrand) params.set('brand', this.selectedBrand);
            if (this.sortBy && this.sortBy !== 'name') params.set('sort', this.sortBy);
            if (this.inStockOnly) params.set('in_stock', '1');

            const queryString = params.toString();
            window.location.href = '/shop' + (queryString ? '?' + queryString : '');
        },

        hasActiveFilters() {
            return this.searchQuery || this.selectedCategory;
        }
    });

    Alpine.data('shopFilterComponent', (initialData) => ({
        selectedCategory: initialData.category || '',
        sortBy: initialData.sort || 'name',
        categories: initialData.categories || [],

        init() {
            const store = Alpine.store('shopFilter');
            if (store) {
                store.selectedCategory = this.selectedCategory;
                store.sortBy = this.sortBy;
            }
        },

        setCategory(category) {
            this.selectedCategory = category;
            Alpine.store('shopFilter').setCategory(category);
        },

        setSort(e) {
            this.sortBy = e.target.value;
            Alpine.store('shopFilter').setSort(this.sortBy);
        }
    }));

    Alpine.data('productFilters', () => ({
        buildUrl(overrides) {
            const url = new URL(window.location.href);
            
            for (const [key, value] of Object.entries(overrides)) {
                if (value === '' || value === null || value === undefined) {
                    url.searchParams.delete(key);
                } else {
                    url.searchParams.set(key, value);
                }
            }
            
            url.searchParams.delete('page');
            return url.toString();
        }
    }));

    Alpine.data('infiniteScroll', (config) => ({
        page: 1,
        perPage: config.perPage || 12,
        loading: false,
        hasMore: true,
        category: config.category || '',
        brand: config.brand || '',
        search: config.search || '',
        sort: config.sort || 'name',
        observer: null,

        init() {
            this.$nextTick(() => {
                this.setupObserver();
            });
        },

        setupObserver() {
            const sentinel = document.getElementById('scroll-sentinel');
            if (!sentinel) return;

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !this.loading && this.hasMore) {
                        this.loadMore();
                    }
                });
            }, {
                root: null,
                rootMargin: '200px',
                threshold: 0
            });

            this.observer.observe(sentinel);
        },

        async loadMore() {
            if (this.loading || !this.hasMore) return;

            this.loading = true;
            this.page++;

            try {
                const response = await frappe.call({
                    method: 'tos_webcommerce.api.product.get_products',
                    args: {
                        page: this.page,
                        per_page: this.perPage,
                        category: this.category,
                        brand: this.brand,
                        search: this.search,
                        sort: this.sort
                    },
                    freeze: false
                });

                if (response.message) {
                    const data = response.message;
                    if (data.products && data.products.length > 0) {
                        this.appendProducts(data.products, data.cards_html);
                        this.hasMore = data.has_more;
                    } else {
                        this.hasMore = false;
                    }
                }
            } catch (error) {
                console.error('Error loading more products:', error);
                this.hasMore = false;
            } finally {
                this.loading = false;
            }
        },

        appendProducts(products, cardsHtml) {
            const grid = document.querySelector('.products-grid');
            if (!grid) return;

            // get_products always returns server-rendered cards_html; keep one source of
            // truth for card markup (templates/includes/product_card.html).
            (cardsHtml || []).forEach((html) => {
                if (html) grid.insertAdjacentHTML('beforeend', html);
            });
        },

        destroy() {
            if (this.observer) {
                this.observer.disconnect();
            }
        }
    }));

    Alpine.data('searchAutocomplete', () => ({
        focused: false,
        query: '',

        get store() {
            return Alpine.store('search') || {
                suggestions: [],
                recentSearches: [],
                isLoading: false,
                query: '',
                search: () => {},
                open: () => {},
                close: () => {},
                submit: () => {},
                clearRecentSearches: () => {}
            };
        },

        onInput(e) {
            this.query = e.target.value;
            this.store.search(this.query);
        },

        onFocus() {
            this.focused = true;
            this.store.open();
        },

        onBlur() {
            setTimeout(() => {
                this.focused = false;
                this.store.close();
            }, 200);
        },

        submit() {
            this.store.query = this.query;
            this.store.submit();
        },

        selectSuggestion(item) {
            window.location.href = item.route || `/shop/${item.name}`;
        },

        selectRecentSearch(query) {
            this.query = query;
            this.store.query = query;
            this.store.submit();
        }
    }));

    Alpine.data('variantSelector', (itemCode, itemName) => ({
        attributes: [],
        selected: {},
        validOptions: {},
        selectedVariant: null,
        variantProductInfo: null,
        variantStockInfo: null,
        variantStock: {},
        publishedVariants: {},
        defaultAttributes: {},
        filteredCount: 0,
        loading: false,
        addingToCart: false,
        qty: 1,

        async init() {
            if (typeof frappe === 'undefined' || !frappe.csrf_token) {
                await new Promise(resolve => {
                    if (typeof frappe !== 'undefined' && frappe.ready) {
                        frappe.ready(resolve);
                    } else {
                        setTimeout(resolve, 500);
                    }
                });
            }

            this.loading = true;
            try {
                const data = await this.fetchAttributes();
                if (data && data.attributes && Array.isArray(data.attributes)) {
                    this.attributes = data.attributes.map((attr, attrIdx) => ({
                        ...attr,
                        attribute: attr.attribute || `attr_${attrIdx}`,
                        values: (attr.values || []).map((val, valIdx) => ({
                            ...val,
                            value: val.value || '',
                            _key: `${attr.attribute}_${val.value}_${valIdx}`
                        }))
                    }));
                    this.variantStock = data.variant_stock || {};
                    this.publishedVariants = data.published_variants || {};
                    this.defaultAttributes = data.default_attributes || {};
                } else {
                    this.attributes = [];
                    this.variantStock = {};
                    this.publishedVariants = {};
                }
            } catch (error) {
                console.error('Failed to load variant attributes:', error);
                this.attributes = [];
                this.variantStock = {};
                this.publishedVariants = {};
            } finally {
                this.loading = false;
            }

            if (Object.keys(this.defaultAttributes).length) {
                this.selected = { ...this.defaultAttributes };
                await this.updateValidOptions();
            }
        },

        async fetchAttributes() {
            if (typeof frappe === 'undefined' || !frappe.call) {
                console.warn('Frappe not available for variant selector');
                return { attributes: [], variant_stock: {}, published_variants: {} };
            }
            try {
                const response = await frappe.call({
                    method: 'tos_webcommerce.api.product.get_published_variant_attributes',
                    args: { item_code: itemCode },
                    freeze: false
                });
                return response.message || { attributes: [], variant_stock: {}, published_variants: {} };
            } catch (error) {
                console.error('Error fetching variant attributes:', error);
                return { attributes: [], variant_stock: {}, published_variants: {} };
            }
        },

        async selectAttribute(attr, value) {
            if (this.selected[attr] === value) {
                delete this.selected[attr];
            } else {
                this.selected[attr] = value;
            }
            await this.updateValidOptions();
        },

        async updateValidOptions() {
            if (Object.keys(this.selected).length === 0) {
                this.validOptions = {};
                this.selectedVariant = null;
                this.variantProductInfo = null;
                this.variantStockInfo = null;
                this.filteredCount = 0;
                window.dispatchEvent(new CustomEvent('variant-image-reset'));
                return;
            }

            if (typeof frappe === 'undefined' || !frappe.call) {
                console.warn('Frappe not available for variant options update');
                return;
            }

            this.loading = true;
            try {
                const response = await frappe.call({
                    method: 'tos_webcommerce.api.product.get_filtered_variants',
                    args: {
                        item_code: itemCode,
                        selected_attributes: JSON.stringify(this.selected)
                    },
                    freeze: false
                });

                const data = response.message;
                this.validOptions = data.valid_options || {};
                this.filteredCount = data.variants ? data.variants.length : 0;

                if (data.exact_match) {
                    this.selectedVariant = data.exact_match;
                    this.variantProductInfo = data.product_info;

                    const variantData = data.variants.find(v => v.item_code === data.exact_match);
                    if (variantData) {
                        this.variantStockInfo = {
                            in_stock: variantData.in_stock,
                            stock_qty: variantData.stock_qty
                        };
                    }

                    if (variantData && variantData.website_image) {
                        window.dispatchEvent(new CustomEvent('variant-image-change', {
                            detail: {
                                image: variantData.website_image,
                                name: variantData.web_item_name || this.selectedVariant
                            }
                        }));
                    }
                } else {
                    this.selectedVariant = null;
                    this.variantProductInfo = null;
                    this.variantStockInfo = null;
                    window.dispatchEvent(new CustomEvent('variant-image-reset'));
                }
            } catch (error) {
                console.error('Failed to update variant options:', error);
            } finally {
                this.loading = false;
            }
        },

        async fetchStockInfo(variantItemCode) {
            try {
                const response = await frappe.call({
                    method: 'tos_webcommerce.api.product.get_item_stock_info',
                    args: { item_code: variantItemCode },
                    freeze: false
                });
                this.variantStockInfo = response.message || null;
            } catch (error) {
                console.error('Failed to fetch stock info:', error);
                this.variantStockInfo = null;
            }
        },

        isSelected(attr, val) {
            return this.selected[attr] === val;
        },

        isDisabled(attr, val) {
            if (Object.keys(this.selected).length === 0) {
                const attrData = this.attributes.find(a => a.attribute === attr);
                if (attrData) {
                    const valData = attrData.values.find(v => v.value === val);
                    return valData && !valData.has_stock;
                }
                return false;
            }

            if (this.selected[attr]) {
                return false;
            }

            if (this.validOptions[attr]) {
                return !this.validOptions[attr].includes(val);
            }

            return false;
        },

        hasStock(attr, val) {
            const attrData = this.attributes.find(a => a.attribute === attr);
            if (attrData) {
                const valData = attrData.values.find(v => v.value === val);
                return valData ? valData.has_stock : false;
            }
            return false;
        },

        clearSelections() {
            this.selected = {};
            this.validOptions = {};
            this.selectedVariant = null;
            this.variantProductInfo = null;
            this.variantStockInfo = null;
            this.filteredCount = 0;
            window.dispatchEvent(new CustomEvent('variant-image-reset'));
        },

        canAddToCart() {
            if (!this.selectedVariant) return false;
            if (!this.variantProductInfo) return false;
            const stockInfo = this.variantStockInfo || this.variantProductInfo;
            const inStock = stockInfo.in_stock === 1 || stockInfo.in_stock === true || stockInfo.stock_qty > 0;
            const onBackorder = this.variantProductInfo.on_backorder;
            return inStock || onBackorder;
        },

        async addToCart() {
            const isLoggedIn = (typeof frappe !== 'undefined') &&
                               frappe.session &&
                               frappe.session.user &&
                               frappe.session.user !== 'Guest';

            if (!isLoggedIn) {
                frappe.show_alert({
                    message: 'Silakan login untuk menambahkan ke keranjang',
                    indicator: 'orange'
                });
                window.location.href = '/login?redirect-to=' + encodeURIComponent(window.location.pathname);
                return;
            }

            if (!this.selectedVariant) return;

            this.addingToCart = true;
            try {
                await frappe.call({
                    method: 'tos_webcommerce.api.cart.update_cart',
                    args: {
                        item_code: this.selectedVariant,
                        qty: this.qty
                    },
                    freeze: true,
                    freeze_message: 'Menambahkan ke keranjang...'
                });

                localStorage.removeItem(`configure:${itemCode}`);

                const cartStore = Alpine.store('cart');
                if (cartStore && cartStore.fetchCart) {
                    await cartStore.fetchCart();
                }

                frappe.show_alert({
                    message: 'Produk ditambahkan ke keranjang',
                    indicator: 'green'
                });
            } catch (error) {
                frappe.show_alert({
                    message: 'Gagal menambahkan ke keranjang',
                    indicator: 'red'
                });
            } finally {
                this.addingToCart = false;
            }
        },

        toggleWishlist() {
            const code = this.selectedVariant || itemCode;
            const wishlistStore = Alpine.store('wishlist');
            if (wishlistStore && wishlistStore.toggle) {
                wishlistStore.toggle(code);
            }
        },

        isInWishlist() {
            const code = this.selectedVariant || itemCode;
            const wishlistStore = Alpine.store('wishlist');
            if (wishlistStore && wishlistStore.has) {
                return wishlistStore.has(code);
            }
            return false;
        }
    }));
}

document.addEventListener('alpine:init', registerAlpineComponents);

if (typeof Alpine !== 'undefined') {
    if (Alpine.version) {
        registerAlpineComponents();
    }
}

document.addEventListener('DOMContentLoaded', () => {
    if (typeof Alpine !== 'undefined' && !Alpine.store('cart')) {
        registerAlpineComponents();
    }
});

window.formatRupiah = function(amount) {
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

if (!document.getElementById('alpine-animations')) {
    const style = document.createElement('style');
    style.id = 'alpine-animations';
    style.textContent = `
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
        }
        .hover-lift {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .hover-lift:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }
        [x-cloak] { display: none !important; }
    `;
    document.head.appendChild(style);
}
